import { HubConnectionState } from '@microsoft/signalr';
import { ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import { Modal as RSModal } from 'reactstrap';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import { BulkEditPreviewListingDetails } from 'src/components/common/BulkActions/BulkEditStatus/components/BulkEditPreviewDisplay';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import {
  BulkEditStage,
  useBulkEditHubContext,
} from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { useContent } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { vars } from 'src/core/themes';
import { Stack, Switch } from 'src/core/ui';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { useSearchV2DataRefetch } from 'src/hooks/useSearchV2DataRefetch';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { MergeSectionTypePromptWithVenueMap } from 'src/modals/MergeListings';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  Event,
  Feature,
  MergedSectionTypeResolution,
  MergeSectionTypeResolutionGroup,
  VenueMapSectionType,
} from 'src/WebApiController';

import { BulkEditFooter } from '../common/BulkEditFooter';
import { BulkEditHeader } from '../common/BulkEditHeader';

export type BulkMergeSelectedListingsDialogProps = ComponentProps<
  typeof RSModal
> & {
  updateKey: string;
  onOkay: (
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreviewWithDetails,
    excludeHeldListings?: boolean
  ) => void;
  onCancel: () => void;
};

export function BulkMergeSelectedListingsDialog({
  updateKey,
  onOkay,
  onCancel,
  ...rest
}: BulkMergeSelectedListingsDialogProps) {
  const entityType = ActionOutboxEntityType.Listing;

  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const {
    eventsTransformed,
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();
  const { searchV2DataDelayedRefetch } = useSearchV2DataRefetch();
  const [isLoading, setIsLoading] = useState(false);

  const hasDefaultNotToMergeHoldListingsFeature = useUserHasFeature(
    Feature.DefaultNotToMergeHoldListings
  );

  const hasEnableOfficeFeature = useUserHasFeature(Feature.EnableOffice);
  const officeMismatchMsg = useContent(
    ContentId.MergeListings_BulkStorefrontMismatchWarning
  );

  // POS-7295: the section type disambiguation only applies when the GA merged-max-display feature is on.
  const hasGaMergedMaxDisplayFeature = useUserHasFeature(
    Feature.GeneralAdmissionMaxDisplayQuantity
  );

  const [excludeHeldListings, setExcludeHeldListings] = useState<boolean>(
    hasDefaultNotToMergeHoldListingsFeature
  );

  // POS-7295: section type the seller picked for each ambiguous merge group, keyed by anchor listing id.
  const [selectedSectionTypes, setSelectedSectionTypes] = useState<
    Record<number, VenueMapSectionType>
  >({});

  // POS-7295: map a listing to its event so each ambiguous group's section picker can use that event's venue map.
  const eventByListingId = useMemo(() => {
    const map = new Map<number, Event>();
    eventsTransformed?.forEach((eventWithData) => {
      eventWithData.entities?.listings?.forEach((listing) => {
        map.set(listing.id, eventWithData.event);
      });
    });
    return map;
  }, [eventsTransformed]);

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);

        await searchV2DataDelayedRefetch();

        if (refreshExpandedListItems) {
          await refreshExpandedListItems();
        }
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }
        setIsRefreshing(false);
        // progress kept to allow the BulkEditStatusPopover to show the result
      }
      onCancel();
    },
    [
      onCancel,
      refreshExpandedListItems,
      searchV2DataDelayedRefetch,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        onClose(doneProgress, false);
      }
    },
    [onClose]
  );

  const { bulkEditHub, progress, preview, initJob, stage } = useBulkEditHub(
    entityType,
    BulkActionType.MergeSelectedListings,
    updateKey,
    onBulkEditDone
  );

  // POS-7295: merge groups from the preview that need the seller to disambiguate the section type.
  // The preview computes these whenever the backend resolves them, so only surface them when the
  // feature is enabled for this account (the same flag gates the backend merge behaviour).
  const sectionTypeGroups = useMemo<MergeSectionTypeResolutionGroup[]>(
    () =>
      hasGaMergedMaxDisplayFeature
        ? (preview?.mergeSectionTypeResolutionGroups ?? [])
        : [],
    [preview, hasGaMergedMaxDisplayFeature]
  );

  const allSectionTypesSelected = useMemo(
    () =>
      sectionTypeGroups.every(
        (group) => selectedSectionTypes[group.listingIds[0]] != null
      ),
    [sectionTypeGroups, selectedSectionTypes]
  );

  const supportBackgroundProcess = useMemo(
    () => bulkEditHub?.state === HubConnectionState.Connected,
    [bulkEditHub?.state]
  );

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        onOkay(false, onPreviewReceived);
      } else {
        // POS-7295: don't merge until every ambiguous group has a section type selected.
        if (!allSectionTypesSelected) {
          setIsLoading(false);
          return;
        }

        if (preview) {
          preview.preview.mergedSectionTypeIds = { ...selectedSectionTypes };
        }

        onOkay(
          supportBackgroundProcess,
          undefined,
          preview,
          excludeHeldListings
        );
      }
      if (!supportBackgroundProcess) {
        setIsLoading(false);
      }
    },
    [
      onOkay,
      supportBackgroundProcess,
      preview,
      excludeHeldListings,
      allSectionTypesSelected,
      selectedSectionTypes,
    ]
  );

  const [isRefreshing, setIsRefreshing] = useState(false);

  const submitButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <GenericDialog
      {...rest}
      size={stage === BulkEditStage.Preview ? 'xl' : 'md'}
      header={
        <BulkEditHeader headerText={<Content id={ContentId.MergeSelected} />} />
      }
      onOpened={initJob}
      onClick={(e) => {
        // This so that this dialog does not cause event to flow to the EventHeader open/close event
        e.stopPropagation();
        e.preventDefault();
      }}
      onClosed={() => {
        setMainDialogOpened(false);
        if (progress) {
          setSelectionMode(undefined);
        }
        // Call the outside one if there is one
        rest.onClosed?.();
      }}
      footer={
        isRefreshing ? null : (
          <BulkEditFooter
            entityType={entityType}
            isLoading={isLoading}
            hasBackgroundBulkEditFeature={supportBackgroundProcess}
            onClose={onClose}
            onSubmit={onSubmitHandler}
            submittButtonRef={submitButtonRef}
            // POS-7295: don't let the seller confirm until every ambiguous merge group has a section type.
            disabled={!allSectionTypesSelected}
          />
        )
      }
      onCancel={isLoading ? undefined : onCancel}
    >
      <BulkEditStatus
        entityType={entityType}
        isLoading={isRefreshing || isLoading}
        updateKey={updateKey}
        entityUpdatesPreview={
          // POS-7295: the section picker is only meaningful once the preview is available, so render it
          // via entityUpdatesPreview (shown in the Preview stage) rather than as a child (Idle stage only).
          sectionTypeGroups.length > 0 ? (
            <Stack direction="column" width="full" gap="m">
              {sectionTypeGroups.map((group, index) => (
                <Stack
                  direction="column"
                  gap="s"
                  key={`merge-section-type-${index}`}
                >
                  <div>
                    <Content
                      id={
                        group.resolution ===
                        MergedSectionTypeResolution.RequiresSourceSelection
                          ? ContentId.MergeListings_SectionTypeDifference
                          : ContentId.MergeListings_SectionMapping
                      }
                    />
                  </div>
                  {/* POS-7295: show which listings this picker applies to, since several groups can
                      each need a section. */}
                  <BulkEditPreviewListingDetails
                    preview={preview}
                    listingIds={group.listingIds}
                    compact
                  />
                  <MergeSectionTypePromptWithVenueMap
                    event={eventByListingId.get(group.listingIds[0])}
                    resolution={group.resolution}
                    candidates={group.candidates}
                    selectedSectionTypeId={
                      selectedSectionTypes[group.listingIds[0]]
                    }
                    onSelect={(sectionTypeId) =>
                      setSelectedSectionTypes((prev) => ({
                        ...prev,
                        [group.listingIds[0]]: sectionTypeId,
                      }))
                    }
                  />
                </Stack>
              ))}
            </Stack>
          ) : undefined
        }
      >
        <Stack direction="column" width="full" gap="m">
          <Content id={ContentId.MergeSelectedListingsConfirmation} />

          {hasEnableOfficeFeature && (
            <WarningMessage message={officeMismatchMsg} />
          )}

          {hasDefaultNotToMergeHoldListingsFeature && (
            <Stack
              direction="row"
              gap="s"
              alignItems="center"
              style={{
                fontSize: vars.typography.fontSize.base,
                fontWeight: 500,
              }}
            >
              <Switch
                checked={!excludeHeldListings}
                onChange={(e) => e.stopPropagation()}
                onCheckedChange={(isChecked) => {
                  if (isChecked === excludeHeldListings) {
                    setExcludeHeldListings(!isChecked);
                  }
                }}
              />
              <Content id={ContentId.IncludeHeldListings} />
            </Stack>
          )}
        </Stack>
      </BulkEditStatus>
    </GenericDialog>
  );
}
